import { debounce } from 'lodash-decorators';
import { appInjector } from '../../app-injector';
import * as constants from '../../constants-types/constants';
import { SortType, SortDirection } from '../../constants-types/Enums';
import { List } from '../../constants-types/PiList.model';
import {
    XFilterType,
    FilterInfo
} from '../../core-data-model/FilterInfo.model';
import { Job } from '../../core-data-model/Job.model';
import { Person } from '../../core-data-model/Person.model';
import { SearchResult } from '../../core-data-model/SearchResult.model';
import { SearchResultSet } from '../../core-data-model/SearchResultSet';
import { SearchState } from '../../core-data-model/SearchState.model';
import { ContextDataService } from '../../core-services/Context.dataservice';
import { CsvConversionService } from '../../core-services/CsvConversion.service';
import { ColumnInfoContainer } from '../../core-services/Tables/ColumnInfo';
import { wait } from '../../core-utilities/AsyncDecorator';

export type ColumnInfo = {
    tableId: string;
    columnId: string;
    headerKey?: string;
};

export abstract class Table<T> {
    id: string; // This id is used as a key into suer profile table settings
    searchState: SearchState;
    possibleFilters: FilterInfo[] = [];
    loading: boolean;
    resultsLoaded = false;
    copyContext: ContextDataService;
    columnInfo: ColumnInfoContainer;
    columnSet: ColumnInfo[] = [];

    private tableContents: T[];
    private columnHash: any = {};

    constructor(id: string) {
        this.id = id;
        this.copyContext = appInjector().get(ContextDataService);
        if (this.id) {
            this.columnInfo = new ColumnInfoContainer(this.id);
        }
    }

    get noResultsFound() {
        return this.resultsLoaded && !this.loading && this.contents.length < 1;
    }
    get contents() {
        return this.tableContents;
    }
    set contents(c) {
        this.tableContents = c;
    }

    @debounce(constants.multipleSendClicksDelay)
    async waitAndReload() {
        await wait(constants.waitForIndex);
        return this.search();
    }

    search() {
        this.loading = true;
        return this.internalSearch(this.searchState)
            .then((results) => {
                this.contents = results;
                return this.contents;
            })
            .finally(() => {
                this.resultsLoaded = true;
                this.loading = false;
            });
    }

    /*
    This is an odd function typing wise because it is an intersection between
    the table data structure and the result set data structure. Right
    now these two generics have different typing constraints since the
    ResultSet entires always have an id. This could be fixed by loosening
    the restrictions on ResultSet and simply having it take an equality or
    identity function in its constructor. Since it would only be to fix this
    function I don't think it is worth it right now (1/15/20), but if the table
    and result set get closer then it might be worth it.
    */
    copyAllToContext(
        selection: SearchResultSet = this.copyContext.currentSet
    ): Promise<SearchResultSet> {
        const searchState = this.searchState
            .setPage(1)
            .setPageSize(this.searchState.pageState.maxResults);
        this.loading = true;
        return this.internalSearch(searchState)
            .then((results: any[]) => selection.addRowSet(results))
            .finally(() => {
                this.loading = false;
            });
    }

    setFilter(filter: FilterInfo, goToPageOne = false) {
        this.searchState = this.searchState.setFilter(filter);
        if (goToPageOne) {
            this.searchState = this.searchState.setPage(1);
        }
        return this.search();
    }
    addSort(sort: SortType, direction?: SortDirection) {
        this.searchState = this.searchState.addSort(sort, direction);
        return this.search();
    }
    // because sometimes we need to add more than one sort and we don't want the search triggered on each add
    addSortWithoutSearch(sort: SortType, direction?: SortDirection) {
        this.searchState = this.searchState.addSort(sort, direction);
    }
    resetSort() {
        this.searchState = this.searchState.clearSort();
    }

    changePage(pageNumber: number) {
        this.searchState.pageState.currentPageNumber = pageNumber;
        return this.search();
    }

    addFilter(filter: FilterInfo): void {
        this.searchState.pageState.currentPageNumber = 1;
        this.searchState = this.searchState.addFilter(filter);
        this.search();
    }
    removeFilter(filter: FilterInfo): void {
        this.searchState = this.searchState.removeFilter(filter);
        this.search();
    }

    removeFiltersOfType(type: XFilterType) {
        this.searchState = this.searchState.removeFiltersOfType(type);
        this.search();
    }

    getName(columnId: string): ColumnInfo {
        return { tableId: this.id, columnId };
    }

    getTitle(columnId: string, headerKey: string) {
        if (!this.columnHash[columnId]) {
            this.columnHash[columnId] = true;
            this.columnSet.push({ tableId: this.id, columnId, headerKey });
        }
        return { tableId: this.id, columnId, headerKey };
    }

    prepCsv(results: List<SearchResult>, _job: Job): any[] {
        const csv: CsvConversionService =
            appInjector().get(CsvConversionService);
        return results
            .map((result) => {
                const retval: any = {};
                csv.addResultTypeColumn(retval, result);
                csv.addResultNameColumn(retval, result);
                csv.addEmailColumn(retval, result);
                csv.addResultJobTitleColumn(retval, result);
                csv.addLastUpdatedColumn(retval, result);
                csv.addPersonTypeColumn(retval, result);
                csv.addBehavioralColumn(retval, result);
                csv.addReferenceProfileNameColumn(retval, result);
                csv.addCognitiveColumn(retval, result);
                csv.addResultBehaviorScoreIdColumn(retval, result);
                csv.addAdministeredByBehavioralColumn(retval, result);
                csv.addAdministeredByCognitiveColumn(retval, result);
                return retval;
            })
            .toArray();
    }

    /**
     * Gets the tooltip of the reference pattern of the provided table entry,
     * but only if that table entry is a `Person` type.
     */
    getTooltipContentKey(result: T) {
        return result instanceof Person && result.referencePattern
            ? result.referencePattern.tooltipKey
            : '';
    }

    clearContents() {
        this.contents = [];
    }

    // Musings - this could potentially be abstract - might better design
    protected internalSearch(searchState: SearchState): Promise<T[]> {
        throw new Error(
            `Can't call search on base Table class. Search state was:\n${JSON.stringify(
                searchState,
                null,
                2
            )}`
        );
    }
}
